import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import CommonTable from "../../../common/table/ComonTable";

const Notices = () => {
    const navigate = useNavigate();
    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, text: '' });
    
    // Filters
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    
    const [currentPage, setCurrentPage] = useState(1);
    const [pageInfo, setPageInfo] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalPages: 1,
        totalRecords: 0
    });

    const employeeCode = storageService.getItem("employeeCode");
    const [siteId, setSiteId] = useState(null);

    const fetchNotices = async () => {
        setLoading(true);
        try {
            let currentSiteId = siteId;
            
            if (!currentSiteId && employeeCode) {
                const userRes = await apiServices({
                    url: '/ResidentialUser/GetResidentialUserById',
                    method: 'POST',
                    data: { userId: employeeCode }
                });
                if (userRes.success && userRes.data?.data?.[0]) {
                    currentSiteId = userRes.data.data[0].siteId;
                    setSiteId(currentSiteId);
                }
            }

            if (!currentSiteId) {
                setNotices([]);
                setLoading(false);
                return;
            }

            const res = await apiServices({
                url: `/DailyNews/GetDailyNewsBySiteId/${currentSiteId}`,
                method: 'GET'
            });

            if (res.success && res.data?.IsSuccess && Array.isArray(res.data.dailyNews)) {
                const mapped = res.data.dailyNews.map((item, i) => ({
                    id: item.Id,
                    newsTitle: item.newsTitle || '',
                    discription: item.discription || '',
                    status: item.IsActive || 'Inactive',
                    originalIndex: i
                }));
                mapped.sort((a, b) => b.id - a.id);
                setNotices(mapped);
                setPageInfo(prev => ({
                    ...prev,
                    totalRecords: mapped.length,
                    totalPages: Math.ceil(mapped.length / prev.pageSize)
                }));
            } else {
                setNotices([]);
            }
        } catch (error) {
            console.error("Error fetching notices:", error);
            toast.error("Failed to fetch notices");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotices();
    }, []);

    // Listen to custom event from NewsLettersMain
    useEffect(() => {
        const handleOpenForm = () => {
            navigate('/zentres/News-Letters/Notice/add');
        };
        window.addEventListener('openNoticeForm', handleOpenForm);
        return () => window.removeEventListener('openNoticeForm', handleOpenForm);
    }, [navigate]);

    const handleLimitChange = (newLimit) => {
        setPageInfo(prev => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
        setCurrentPage(1);
    };

    const handleDelete = async (row) => {
        toast.info("Delete functionality not currently supported by API.");
    };

    const handleEdit = (row) => {
        navigate(`/zentres/News-Letters/Notice/edit/${row.id}`, { state: { item: row } });
    };

    const columns = [
        { key: "no", label: "No", align: "left" },
        { key: "newsTitle", label: "Title", align: "left" },
        { key: "discription", label: "Description", align: "left" },
        { key: "status", label: "Status", align: "center" },
        { key: "actions", label: "Actions", align: "center" }
    ];

    const renderCell = (key, row, index) => {
        switch (key) {
            case "no":
                return <span className="text-[13px] font-medium text-[#64748B]">{index + 1 + (currentPage - 1) * pageInfo.pageSize}</span>;
            case "newsTitle":
                return <span className="text-[13px] font-bold text-gray-800 tracking-tight">{row.newsTitle || "—"}</span>;
            case "discription":
                if (row.discription && row.discription.length > 20) {
                    return (
                        <span
                            className="block max-w-[200px] truncate overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-medium text-[#64748B] cursor-pointer underline decoration-dotted underline-offset-2"
                            onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltip({
                                    visible: true,
                                    x: rect.left + rect.width / 2,
                                    y: rect.top - 8,
                                    text: row.discription
                                });
                            }}
                            onMouseLeave={() => setTooltip(prev => ({ ...prev, visible: false }))}
                        >
                            {row.discription}
                        </span>
                    );
                }
                return <span className="text-[13px] font-medium text-[#64748B]">{row.discription || "—"}</span>;
            case "status":
                const status = row.status || 'Inactive';
                let statusColor = "bg-gray-100 text-gray-600";
                if (status === 'Active') statusColor = "bg-[#DCFCE7] text-[#166534]";
                if (status === 'Inactive') statusColor = "bg-[#FEF9C3] text-[#D97706]";
                return (
                    <span className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold shadow-sm ${statusColor}`}>
                        {status}
                    </span>
                );
            case "actions":
                return null;
            default:
                return <span className="text-[13px] font-medium text-[#64748B]">{row[key] || "—"}</span>;
        }
    };

    const filteredData = useMemo(() => {
        let data = notices;
        if (searchTerm) {
            data = data.filter(item => 
                (item.newsTitle || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                (item.discription || "").toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        if (statusFilter) {
            data = data.filter(item => item.status === statusFilter);
        }
        return data;
    }, [notices, searchTerm, statusFilter]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageInfo.pageSize;
        return filteredData.slice(start, start + pageInfo.pageSize);
    }, [filteredData, currentPage, pageInfo.pageSize]);

    const headerAction = (
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-60">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                <input
                    type="text"
                    placeholder="Search Notices..."
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    className="w-full pl-9 pr-4 py-2 border border-gray-200 bg-[#F8FAFC] rounded-lg text-xs font-medium focus:outline-none focus:border-blue-500 focus:bg-white transition-colors"
                />
            </div>
            
            <select
                value={statusFilter}
                onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                }}
                className="w-full sm:w-40 px-3 py-2 border border-gray-200 bg-[#F8FAFC] rounded-lg text-xs font-medium focus:outline-none focus:border-blue-500 focus:bg-white transition-colors"
            >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
            </select>
        </div>
    );

    return (
        <div className="w-full h-full flex flex-col gap-4">
            <div className="shrink-0 flex-1 flex flex-col bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <CommonTable
                    headers={columns}
                    headerAction={headerAction}
                    data={paginatedData.map((d, i) => ({ ...d, originalIndex: i }))}
                    renderCell={(key, row) => renderCell(key, row, row.originalIndex)}
                    pagination={{
                        ...pageInfo,
                        pageNumber: currentPage,
                        totalRecords: filteredData.length,
                        totalPages: Math.ceil(filteredData.length / pageInfo.pageSize) || 1
                    }}
                    onPageChange={setCurrentPage}
                    onLimitChange={handleLimitChange}
                    loading={loading}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                />
            </div>

            {/* Tooltip Portal - renders outside table overflow context */}
            {tooltip.visible && createPortal(
                <div
                    className="pointer-events-none fixed z-[99999] transition-opacity duration-150"
                    style={{
                        left: tooltip.x,
                        top: tooltip.y,
                        transform: 'translateX(-50%) translateY(-100%)'
                    }}
                >
                    <div className="bg-[#1E293B] text-white text-xs font-medium py-2.5 px-4 rounded-xl shadow-2xl max-w-sm break-words leading-relaxed whitespace-pre-wrap">
                        {tooltip.text}
                    </div>
                    <div className="w-3 h-3 bg-[#1E293B] rotate-45 mx-auto -mt-1.5"></div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Notices;
